import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { AnimatedButton } from '@/components/ui/animated-button'
import type { Cta } from '@/components/sections/types'
import { cn } from '@/lib/cn'
import { trackEvent } from '@/lib/analytics/load'
import { scrollToAnchor } from '@/lib/anchor'

/** Reports the click when it has a placement; a `#` link scrolls to its section. */
function onCtaClick(cta: Cta) {
  return (e: React.MouseEvent<HTMLElement>) => {
    if (cta.placement) trackEvent('cta_click', { placement: cta.placement, label: cta.label })
    scrollToAnchor(e, cta.href)
  }
}

/**
 * One quiet text link: router `Link` for internal paths, `<a>` for `#` and
 * `http` (external ones open in a new tab). Tracks `cta_click` when it has a placement.
 */
export function CtaLink({ cta, className }: { cta: Cta; className?: string }) {
  const cls = cn('text-blue underline underline-offset-2', className)
  if (/^https?:/.test(cta.href)) {
    return (
      <a href={cta.href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onCtaClick(cta)}>
        {cta.label}
      </a>
    )
  }
  if (cta.href.startsWith('#')) {
    return (
      <a href={cta.href} className={cls} onClick={onCtaClick(cta)}>
        {cta.label}
      </a>
    )
  }
  return (
    <Link to={cta.href} className={cls} onClick={onCtaClick(cta)}>
      {cta.label}
    </Link>
  )
}

/** `.button-lockup` — the 12px-gap row that holds a section's CTAs. */
export function CtaGroup({ ctas, className }: { ctas: Cta[]; className?: string }) {
  if (!ctas.length) return null
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {ctas.map((cta) =>
        cta.kind === 'animated' ? (
          <AnimatedButton key={cta.href + cta.label} href={cta.href}>
            {cta.label}
          </AnimatedButton>
        ) : (
          <Button
            key={cta.href + cta.label}
            asChild
            variant={cta.kind ?? 'primary'}
            onClick={onCtaClick(cta)}
          >
            {/^https?:|^#/.test(cta.href) ? (
              <a href={cta.href}>{cta.label}</a>
            ) : (
              <Link to={cta.href}>{cta.label}</Link>
            )}
          </Button>
        ),
      )}
    </div>
  )
}
